import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/config";

const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-[60] border-b border-white/10 bg-ink/90 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-6 lg:px-12">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
            <img src="/assets/monogram.svg" alt="" aria-hidden="true" className="h-9 w-9" />
            <span className="font-display text-sm font-bold tracking-[0.14em] text-white">
              {SITE.name}
            </span>
          </Link>

          <nav className="hidden items-center gap-8 xl:flex" aria-label="Navigazione principale">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 transition-colors duration-300 hover:text-white"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={SITE.bf360}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-analisi"
              className="btn-gold group hidden items-center gap-2 px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] sm:inline-flex"
            >
              Fai il BF360
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Chiudi menu" : "Apri menu"}
              className="text-white xl:hidden"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink px-8 xl:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Menu mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block py-2 font-display text-4xl font-bold tracking-tight text-white transition-colors duration-300 hover:text-accent"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="mt-8"
              >
                <a
                  href={SITE.bf360}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-cta-analisi"
                  className="btn-gold inline-flex items-center gap-3 px-7 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em]"
                >
                  Fai il BF360
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
